"use client"

import Link from "next/link"
import Image from "next/image"
import { Settings } from "lucide-react"
import { useState, useContext } from "react"
import { useAuth } from "@/contexts/AuthContext"
import { useContactInfo } from "@/hooks/useContactInfo"
import { ContactInfoModal } from "@/components/admin/contact-info-modal"
import type { ContactInfoResDTO } from "@/types/api"

// Import the context directly to check if it exists
import { ContactInfoContext } from "@/contexts/ContactInfoContext"

export function Footer() {
  const { isAuthenticated } = useAuth()
  
  // Try to use the context first, fall back to hook if context is not available
  const context = useContext(ContactInfoContext)
  const hookData = useContactInfo({ autoFetch: !context }) // Only fetch if no context
  
  const { contactInfo, setContactInfo, refetch } = context || hookData
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Ciudades específicas con sus IDs del API
  const cities = [
    { id: 1, name: "Valencia" },
    { id: 2, name: "Pamplona" },
    { id: 3, name: "Salamanca" },
  ]

  const handleContactInfoUpdate = (updatedInfo: ContactInfoResDTO) => {
    // Update the local state first
    setContactInfo(updatedInfo)
    
    // Then trigger a refetch to ensure all components are synchronized
    // Use setTimeout to ensure the state update happens first
    setTimeout(() => {
      if (refetch) {
        refetch()
      }
    }, 50)
  }

  return (
    <footer className="w-full py-12 md:py-16 bg-raime-dark-blue text-white">
      <div className="container px-4 md:px-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8">
        {/* Company Info */}
        <div className="space-y-4">
          <Link href="/" className="flex items-center gap-2 text-lg font-semibold">
            <Image 
              src="/logoDark.svg" 
              alt="Raime Rentals Logo" 
              width={120} 
              height={40}
              className="object-contain"
            />
          </Link>
          <p className="text-sm text-gray-300">
            &copy; {new Date().getFullYear()} Raime Rentals. Todos los derechos reservados.
          </p>
        </div>

        {/* Cities */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-raime-golden-brown">Ciudades Populares</h3>
          <nav className="space-y-2 flex flex-col">
            {cities.map((city) => (
              <Link
                key={city.id}
                href={`/propiedades?cityId=${city.id}`}
                className="text-sm text-gray-300 hover:underline hover:text-raime-golden-brown transition-colors"
              >
                {city.name}
              </Link>
            ))}
          </nav>
        </div>

        {/* Quick Links */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-raime-golden-brown">Enlaces Rápidos</h3>
          <nav className="space-y-2 flex flex-col">
            <Link href="/" className="text-sm text-gray-300 hover:underline hover:text-raime-golden-brown">
              Inicio
            </Link>
            <Link href="#features" className="text-sm text-gray-300 hover:underline hover:text-raime-golden-brown">
              Ventajas
            </Link>
            <Link href="/propiedades" className="text-sm text-gray-300 hover:underline hover:text-raime-golden-brown">
              Propiedades
            </Link>
            <Link href="#contact" className="text-sm text-gray-300 hover:underline hover:text-raime-golden-brown">
              Contacto
            </Link>
          </nav>
        </div>

        {/* Contact Info */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-raime-golden-brown">Contáctanos</h3>
            {isAuthenticated && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="p-1 hover:bg-white/10 rounded-md transition-colors"
                title="Editar información de contacto"
              >
                <Settings className="h-4 w-4 text-gray-300 hover:text-white" />
              </button>
            )}
          </div>
          <p className="text-sm text-gray-300">
            Email: {contactInfo.displayEmail}
            <br />
            Teléfono: {contactInfo.displayPhone}
            <br />
          </p>
        </div>
      </div>

      {/* Legal Links */}
      <div className="border-t border-gray-700 mt-8 pt-6">
        <div className="container px-4 md:px-6">
          <div className="flex justify-center items-center space-x-4 text-sm text-gray-300">
            <Link href="/politica-privacidad" className="hover:text-raime-golden-brown transition-colors">
              Política de Privacidad
            </Link>
            <span>•</span>
            <Link href="/aviso-legal" className="hover:text-raime-golden-brown transition-colors">
              Aviso Legal
            </Link>
          </div>
        </div>
      </div>

      {/* Contact Info Modal */}
      {isAuthenticated && (
        <ContactInfoModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSuccess={handleContactInfoUpdate}
          initialData={contactInfo}
        />
      )}
    </footer>
  )
}
